import React, { useState, useEffect } from "react";
import {
  ButtonRed,
  FormField,
  SelectBox,
  StatusMessage,
} from "../../style/view.styles";
import { fetchEntityData, saveEntityData } from "../../utils/RequestHandler";
import { getLoggedInProfile, getLoggedInUser } from "../../utils/AuthHandler";
import PhoneField from "../Inputs/PhoneInput";
import LoadingSpinner from "./LoadingSpinner";
// import AvatarIcon from "../../assets/svg/AvatarIcon";

const TalentProfileSection = () => {
  const [profile, setProfile] = useState(null);
  const [educationLevels, setEducationLevels] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [formStatus, setFormStatus] = useState({ message: "", type: "" });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEducationLevels = async () => {
      try {
        const response = await fetchEntityData("education_levels");
        if (response.success) {
          setEducationLevels(response.result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchIndustries = async () => {
      try {
        const response = await fetchEntityData("industries");
        if (response.success) {
          setIndustries(response.result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchTalentProfile = async () => {
      const fetchProfile = async () => {
        const defaultUser = { email: getLoggedInUser().username };
        try {
          const user = await getLoggedInProfile();
          setProfile(user ?? defaultUser);
          if (!user) {
            setIsEditing(true);
          }
        } catch (error) {
          console.error("Error fetching talent profile:", error);
          setProfile({ email: getLoggedInUser().username });
        } finally {
          setLoading(false);
        }
      };
      fetchProfile();
    };
    fetchEducationLevels();
    fetchIndustries();
    fetchTalentProfile();
  }, []);

  const handleEditClick = (e) => {
    e.preventDefault();
    setIsEditing(true);
  };

  const handleSaveClick = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setFormStatus({ message: "Saving profile ...", type: "error" });
      const user = getLoggedInUser();
      const requestData = {
        ...profile,
        email: user.username,
      };
      const response = await saveEntityData(
        "talents",
        requestData,
        true,
        profile?.uuid
      );
      if (response.success) {
        setFormStatus({
          message: "Profile saved successfully!",
          type: "success",
        });
      } else {
        setFormStatus({
          message:
            response.message ?? "An error occurred while saving your profile.",
          type: "error",
        });
      }
    } catch (error) {
      setFormStatus({
        message:
          error.message ?? "An error occurred while saving your profile.",
        type: "error",
      });
    } finally {
      setIsEditing(false);
      setSubmitting(false);
    }
  };

  return !loading && profile ? (
    <>
      {/* Avatar & User Info */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          marginBottom: "20px",
        }}
      >
        {/* <div style={{ width: "100px", height: "100px", borderRadius: "50%", overflow: "hidden" }}>
          <AvatarIcon />
        </div> */}

        {/* Editable User Details */}
        <div style={{ flex: 1 }}>
          <FormField>
            <label>First Name</label>
            <input
              type="text"
              value={profile.first_name ?? ""}
              onChange={(e) =>
                setProfile({ ...profile, first_name: e.target.value })
              }
              disabled={!isEditing}
            />
          </FormField>

          <FormField>
            <label>Last Name</label>
            <input
              type="text"
              value={profile.last_name ?? ""}
              onChange={(e) =>
                setProfile({ ...profile, last_name: e.target.value })
              }
              disabled={!isEditing}
            />
          </FormField>

          <FormField>
            <label>Email</label>
            <input
              type="email"
              value={profile.email ?? ""}
              onChange={(e) =>
                setProfile({ ...profile, email: e.target.value })
              }
              disabled={true}
            />
          </FormField>

          <PhoneField
            type="text"
            value={profile.phone ?? ""}
            onChange={(value) => setProfile({ ...profile, phone: value })}
            disabled={!isEditing}
          />
        </div>
      </div>

      {/* Industry & Education */}

      <FormField>
        <label>Preferred Industry</label>
        <SelectBox
          value={profile.preferred_industry ?? ""}
          onChange={(e) =>
            setProfile({ ...profile, preferred_industry: e.target.value })
          }
          disabled={!isEditing}
        >
          <option value="">Select Industry</option>
          {industries.map((option) => (
            <option key={option.name} value={option.id}>
              {option.name}
            </option>
          ))}
        </SelectBox>
      </FormField>

      <FormField>
        <label>Education Level</label>
        <SelectBox
          value={profile.education_level ?? ""}
          onChange={(e) =>
            setProfile({ ...profile, education_level: e.target.value })
          }
          disabled={!isEditing}
        >
          <option value="">Select Education</option>
          {educationLevels.map((option) => (
            <option key={option.level} value={option.id}>
              {option.level}
            </option>
          ))}
        </SelectBox>
      </FormField>

      {/* Bio Section */}
      <FormField>
        <label>Bio</label>
        <textarea
          value={profile.bio ?? ""}
          onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
          disabled={!isEditing}
          rows="4"
        />
      </FormField>

      {/* Profile Details */}
      <form>
        <FormField>
          <label>Location</label>
          <input
            type="text"
            value={profile.location ?? ""}
            onChange={(e) =>
              setProfile({ ...profile, location: e.target.value })
            }
            disabled={!isEditing}
          />
        </FormField>

        {/* Edit / Save Button */}
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          {isEditing ? (
            <ButtonRed onClick={handleSaveClick} disabled={submitting}>
              Save
            </ButtonRed>
          ) : (
            <ButtonRed onClick={handleEditClick}>Edit</ButtonRed>
          )}
        </div>
        {formStatus.message && (
          <StatusMessage type={formStatus.type}>
            {formStatus.message}
          </StatusMessage>
        )}
      </form>
    </>
  ) : (
    <LoadingSpinner />
  );
};

export default TalentProfileSection;
